
import { Component, Inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import { filter } from 'rxjs/operators';

import { FolderService } from '../../../Core/services/folder.service';

import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { AbstractPageBuilderService } from '@Common/PageBuilder/services/abstract-page-builder.service';
import { CuratorBlock } from '@Common/PageBuilder/models/curator-block.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { Mention } from '@Common/Mentions/models/mention';

import { mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum } from '@api/mentions/enum/mention-source-api-types.enum';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '@api/shared/enums/sort-order.enum';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';

import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';

import { BriefingsService } from '../../../Briefings/services/briefings.service';
import { CuratedMentionListDisplayElementModel } from '../../../Briefings/models/curated-mention-list-display-element.model';
import { AnalysisDisplayElementModel } from '../../../Briefings/models/briefing-analysis-display-element.model';

import { MonitoringSeriesTypeEnum } from '../../enums/monitoring-series-type.enum';
import { MentionSearchQueryModel } from '../../models/mention-search-query.model';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { MentionModel } from '@api/mentions/model/mention.model';
import { MonitoringSourcePickerFieldWithoutFacetComponent } from './monitoring-source-picker-field-without-facet.component';
import { MentionsNoFacetFilterService } from '@Common/Mentions/services/mentions-no-facet-filter.service';
import { SearchStatesEnum } from '../../enums/search-states.enum';
import { CachedDateService } from '../../../../Common/MonitorShared/services/cached-date.service';
import { AlertModel } from '@Monitor/Alerts/models/alert.model';
import { AlertTypeEnum } from '@Common/MonitorShared/enum/alert-type.enum';
import { DateInputTypesEnum } from '../../../../Common/Forms/enums/date-input-types.enum';

@Component({
    selector: 'agpr-analysis-block-monitoring-source-picker-field',
    templateUrl: './monitoring-source-picker-field.component.html',
    styleUrls: ['./monitoring-source-picker-field.component.scss'],
    providers: [
        MentionsNoFacetFilterService,
        {
            provide: FilterService,
            useExisting: MentionsNoFacetFilterService
        }
    ],
    standalone: false
})
export class AnalysisBlockMonitoringSourcePickerFieldComponent
    extends MonitoringSourcePickerFieldWithoutFacetComponent implements OnInit, OnDestroy, OnChanges {

    static readonly SURGE_PERIOD_QUERY: DateQueryModel = new DateQueryModel();

    get isSurgePeriodSelected(): boolean {
        return this.dateQuery === AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;
    }

    readonly messageSeverityEnum = messageSeverityEnum;

    @Input() dynamicDisplayDate: boolean = true;

    allCuratedMentionsCount: number;

    initialLoad: boolean = false;
    usingCuratedMentions: boolean = false;
    ignoreInitialized: boolean = false;
    currentResource: any;

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public filterService: MentionsNoFacetFilterService,
        public currentUserStateService: CurrentUserStateService,
        public localizationService: LocalizationService,
        public mentionSearchService: MentionSearchService,
        private pageBuilderService: AbstractPageBuilderService,
        private currencyConversionService: CurrencyConversionService,
        public cachedDateService: CachedDateService
    ) {
        super(
            topicsService,
            monitorConfigService,
            folderService,
            tagsService,
            tagGroupsService,
            mediaSubscriptionService,
            sourceGroupService,
            sourceService,
            themeService,
            agentService,
            appBaseHref,
            filterService,
            currentUserStateService,
            localizationService,
            cachedDateService
        );
    }

    ngOnInit() {
        // Initialize "Coverage in Section" options
        const briefingsService = (this.pageBuilderService as BriefingsService);
        if (typeof briefingsService.getAllMentionBlocks === 'function') {
            this.mentionBlocksOptions = briefingsService.getAllMentionBlocks()?.map(
                block => new DropdownOptionModel(
                    (block.displayElement as CuratedMentionListDisplayElementModel)?.title,
                    (block.displayElement as CuratedMentionListDisplayElementModel)
                )
            );
        }
        this.mentionBlocksLoading = false;

        this.setupSubscriptions();
        this.initializeSelectedDataSource();

        this.initialLoad = true;
    }

    setupSubscriptions() {
        super.setupSubscriptions();
        this.setupSubscriptionsForFilterService();
        this.setupSubscriptionsForMentionSearch();
        const briefingsService = (this.pageBuilderService as BriefingsService);

        if (typeof briefingsService.getCurrentBriefingMentions === 'function') {
            this.subscriptions.push(
                briefingsService.getCurrentBriefingMentions().subscribe(mentions => {
                    this.allCuratedMentionsCount = this.setMentionCount(mentions);
                }));
        }
        this.subscriptions.push(
            this.pageBuilderService.getSelectedBlock()
                .pipe(filter(x => !!x))
                .subscribe(block => {
                    this.selectedBlock = block;
                    if (this.value.type === MonitoringSeriesTypeEnum.coverageInSection) {
                        this.sourceType = this.value.type;
                        this.selectedSource = this.mentionBlocksOptions.find(mentionBlock => {
                            return mentionBlock.value?.blockReferenceId === (block.displayElement as AnalysisDisplayElementModel).sourceBlockReferenceId;
                        });
                        if (this.selectedSource) {
                            this.selectItem(this.selectedSource);
                        }
                    }
                })
        );

        if (this.dynamicDisplayDate) {
            this.subscriptions.push(
                this.pageBuilderService.getFocusedResource().subscribe(resource => {
                    this.currentResource = resource;
                    this.hideDateRecurring = !!resource.sendAt.recurring && !resource.sendAt.allowPastContent;
                    this.updateDateFilterOptions(true);
                })
            );
        }
    }

    setupSubscriptionsForFilterService() {
        if (this.field.orderedAccountFilters) {
            this.filterService.setOrderedAccountFilters(this.field.orderedAccountFilters);
        }
        if (this.field.orderedMercuryFilters) {
            this.filterService.setOrderedMercuryFilters(this.field.orderedMercuryFilters);
        }

        this.subscriptions.push(
            this.filterService.getSearchFilters().pipe(filter(x => !!x)).subscribe((searchCriteria) => {
                this.value.filterSearchCriteria = searchCriteria;
                this.dateQuery = searchCriteria.query.dateQuery;
                const blockDisplayElement = (this.selectedBlock ?? this.block)?.displayElement;
                if ((blockDisplayElement as any)?.useSearchDateFilter === false) {
                    this.dateQuery = AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;
                }
                // need to make sure to also set the dateQuery in mentionSearchService
                this.mentionSearchService.setDateQuery(this.dateQuery);

                this.changes.emit(this.value);
            })
        );

    }

    setupSubscriptionsForMentionSearch() {
        if (this.field.orderedAccountFilters) {
            this.mentionSearchService.setOrderedAccountFilters(this.field.orderedAccountFilters);
        }
        if (this.field.orderedMercuryFilters) {
            this.mentionSearchService.setOrderedMercuryFilters(this.field.orderedMercuryFilters);
        }

        this.subscriptions.push(
            // When filters are applied, listen for the event and perform a mention search.
            this.filterService.getSearchFilters().pipe(filter(x => !!x)).subscribe(filterCriteria => {
                if (this.sourceIsValid()) {
                    // We use filterCriteria from the filter service so no filters need to be set in mention search service
                    this.mentionSearchService.search(false, false, filterCriteria, true);
                }
            }),
            this.mentionSearchService.getLatestSearchCriteria().pipe(filter(x => !!x)).subscribe(searchCriteria => {
                this.value.combinedSearchCriteria = searchCriteria;
                this.changes.emit(this.value);
            }),
            this.mentionSearchService.getMentionCount().subscribe(count => {
                this.setNumberOfResults(count);
            }),
            this.mentionSearchService.getSearchState().subscribe(state => {
                this.isLoadingResults = state.type === SearchStatesEnum.loading;
            })
        );
    }

    /**
     * Initializes the component to select the appropriate datasource type & id if available
     */
    public async initializeSelectedDataSource(): Promise<void> {
        if (!(this.folderOptions &&
            this.topicOptions &&
            this.tagOptions &&
            this.tagGroupOptions &&
            this.mediaSubscriptionOptions &&
            this.sourceGroupOptions &&
            this.socialTopicsOptions &&
            this.socialThemesOptions &&
            this.aiAgentOptions &&
            this.mentionBlocksOptions)
        ) {
            return;
        }
        this.usingCuratedMentions = false;
        if (!this.value) {
            // component is loading without a data source pre selected
            await this.initializeEmptyDataSource();

            return;
        }

        this.sourceType = this.value.type;
        this.initializeDataSourceOptions();
        let sourceLoading: boolean = false;

        switch (this.value.type) {
            case MonitoringSeriesTypeEnum.folder:
                this.selectedSource = this.folderOptions.find((folderOption) => {
                    return folderOption.value.id === this.value.id;
                });
                sourceLoading = this.foldersLoading;
                break;
            case MonitoringSeriesTypeEnum.topic:
                this.selectedSource = this.topicOptions.find((topicOption) => {
                    return topicOption.value.id === this.value.id;
                });
                sourceLoading = this.topicsLoading;
                break;
            case MonitoringSeriesTypeEnum.subscription:
                this.selectedSource = this.mediaSubscriptionOptions.find((mediaSubscriptionOption) => {
                    return mediaSubscriptionOption.value.id === this.value.id;
                });
                sourceLoading = this.subscriptionsLoading;
                break;
            case MonitoringSeriesTypeEnum.tag:
                this.selectedSource = this.tagOptions.find((tagOption) => {
                    return tagOption.value.id === this.value.id;
                });
                sourceLoading = this.tagsLoading;
                break;
            case MonitoringSeriesTypeEnum.tagGroup:
                this.selectedSource = this.tagGroupOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.tagGroupsLoading;
                break;
            case MonitoringSeriesTypeEnum.sourceGroup:
                this.selectedSource = this.sourceGroupOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.sourceGroupsLoading;
                break;
            case MonitoringSeriesTypeEnum.socialTopic:
                this.selectedSource = this.socialTopicsOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.socialTopicsLoading;
                break;
            case MonitoringSeriesTypeEnum.theme:
                this.selectedSource = this.socialThemesOptions.find(tagGroupOption => {
                    return tagGroupOption.value.id === this.value.id;
                });
                sourceLoading = this.themesLoading;
                break;
            case MonitoringSeriesTypeEnum.aiAgent:
                this.selectedSource = this.aiAgentOptions.find(aiAgentOption => {
                    return aiAgentOption.value.id === this.value.id;
                });
                sourceLoading = this.aiAgentsLoading;
                break;
            case MonitoringSeriesTypeEnum.coverageInSection:
                if (this.selectedBlock) {
                    this.selectedSource = this.mentionBlocksOptions?.find(mentionBlock => {
                        // eslint-disable-next-line max-len
                        return mentionBlock.value?.blockReferenceId === (this.selectedBlock?.displayElement as AnalysisDisplayElementModel).sourceBlockReferenceId;
                    });
                }
                sourceLoading = this.mentionBlocksLoading;
                break;
        }
        if (this.value.id && !this.selectedSource && this.selectedBlock && !sourceLoading && this.sourceType !== MonitoringSeriesTypeEnum.coverageSurge) {
            await this.initializeEmptyDataSource();
            this.sourceNotFound.emit(true);
            return;
        } else {
            this.sourceNotFound.emit(false);
        }

        if (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) {
            this.updateDateFilterOptions(true);
            this.suggestions$.next(this.mentionBlocksOptions);

            this.value.id = this.selectedSource?.value?.backendDataSource?.id;
            this.value.referenceId = this.selectedSource?.value?.blockReferenceId;
            delete this.value.dataSourceName;

            if (this.selectedSource?.value?.mentions && !this.selectedSource?.value?.filterMentionSearchModel) {
                this.usingCuratedMentions = true;
                this.setNumberOfResults(this.setMentionCount(this.selectedSource?.value?.mentions));
            } else {
                this.setAutomaticSource(this.selectedSource?.value);
            }

        } else if (this.sourceType === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
            this.usingCuratedMentions = true;
            this.setNumberOfResults(this.allCuratedMentionsCount);
        } else {
            const blockDisplayElement = (this.selectedBlock ?? this.block)?.displayElement;
            const isSurgeAlertContext = this.currentResource && (this.currentResource as AlertModel).alertType === AlertTypeEnum.SURGE;
            if (isSurgeAlertContext
                && this.sourceType === MonitoringSeriesTypeEnum.coverageSurge
                && blockDisplayElement
                && (blockDisplayElement as any).useSearchDateFilter === undefined) {
                (blockDisplayElement as any).useSearchDateFilter = false;
            }

            this.updateDateFilterOptions(true);

            this.getFiltersFromSearch(this.value.filterSearchCriteria);
            this.filterService.setSearchFilters(this.value.filterSearchCriteria);

            this.setSafeTopicDateQuery();
            const isSurgePeriod = (blockDisplayElement as any)?.useSearchDateFilter === false;
            if (!isSurgePeriod && this.value.filterSearchCriteria.query.dateQuery) {
                this.dateQuery = this.value.filterSearchCriteria.query.dateQuery;
            }
            await this.updateSearchCriteria();
            if (this.value.filterSearchCriteria.query.dateQuery) {
                this.setSearchDateQuery(this.value.filterSearchCriteria.query.dateQuery);
            }
            this.search();
        }
    }


    /**
     * Handles change in date range
     * @param dateQuery -  the date to change the selected date to
     */
    public async dateRangeChanged(dateQuery: DateQueryModel): Promise<void> {
        if (this.initialLoad && !this.hideDateRecurring) {
            const isSurgeAlertContext = this.currentResource && (this.currentResource as AlertModel).alertType === AlertTypeEnum.SURGE;
            const isSurgePeriod = dateQuery === AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;
            if (isSurgeAlertContext && this.selectedBlock?.displayElement) {
                (this.selectedBlock.displayElement as any).useSearchDateFilter = !isSurgePeriod;
                if (this.selectedBlock instanceof CuratorBlock) {
                    (this.selectedBlock as CuratorBlock).blocks.forEach(innerBlock => {
                        (innerBlock.displayElement as any).useSearchDateFilter = !isSurgePeriod;
                    });
                }
                this.pageBuilderService.notifyBlockDisplayElementChanged(this.selectedBlock);
            }
            if (!isSurgePeriod) {
                if (this.value?.filterSearchCriteria?.query) {
                    this.value.filterSearchCriteria.query.dateQuery = dateQuery;
                }
                if (this.value?.sourceSearchCriteria?.query) {
                    this.value.sourceSearchCriteria.query.dateQuery = dateQuery;
                }
                super.dateRangeChanged(dateQuery);
            } else {
                this.emitChanges();
            }
        }
    }

    search(): void {
        this.filterService.applyFiltersToSearch();
    }

    /**
     * Selects a type of series
     */
    public async selectType(type: MonitoringSeriesTypeEnum): Promise<void> {
        if (type === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
            this.sourceType = type;
            this.selectedSource = null;
            this.resetFilters();

            this.value.type = this.sourceType;
            this.value.id = null;

            this.setAccountFilters();
            await this.updateSearchCriteria();
            this.clearSearch();

            this.changes.emit(this.value);
            this.setNumberOfResults(this.allCuratedMentionsCount);
        } else if (type === MonitoringSeriesTypeEnum.coverageSurge) {
            this.sourceType = type;
            this.value.type = this.sourceType;
            this.value.id = null;
            this.selectedSource = null;
            this.value.filterSearchCriteria = new MentionSearchCriteriaModel();

            const isSurgeAlertContext = this.currentResource && (this.currentResource as AlertModel).alertType === AlertTypeEnum.SURGE;
            if (isSurgeAlertContext) {
                const blockDisplayElement = (this.selectedBlock ?? this.block)?.displayElement;
                if (blockDisplayElement) {
                    (blockDisplayElement as any).useSearchDateFilter = false;
                }
                this.dateQuery = AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;
            } else {
                const defaultDateQuery = DateQueryModel.getLast24HoursQuery();
                this.value.filterSearchCriteria.query.dateQuery = defaultDateQuery;
                this.dateQuery = defaultDateQuery;
            }

            await this.updateDateFilterOptions(false);
            if (isSurgeAlertContext) {
                await this.dateRangeChanged(this.dateQuery);
            }
            this.changes.emit(this.value);
        } else {
            super.selectType(type);
        }
    }

    public async updateDateFilterOptions(isInitialization: boolean = false): Promise<void> {
        const blockDisplayElement = (this.selectedBlock ?? this.block)?.displayElement;
        const isSurgePeriod = (blockDisplayElement as any)?.useSearchDateFilter === false
            || this.dateQuery === AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;

        if (isSurgePeriod) {
            // Temporarily set a standard date so super doesn't fall back and reset useSearchDateFilter
            this.dateQuery = DateQueryModel.getLast24HoursQuery();
        }

        await super.updateDateFilterOptions(isInitialization);

        if (this.currentResource && (this.currentResource as AlertModel).alertType === AlertTypeEnum.SURGE) {
            this.dateFilterOptions = [
                ...this.dateFilterOptions.filter(
                    option => option.value.query !== AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY
                ),
                new DropdownOptionModel(
                    this.localizationService.transform('Monitoring.sourcePickerField.surgePeriod'),
                    { query: AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY }
                )
            ];
            if (isSurgePeriod) {
                this.dateQuery = AnalysisBlockMonitoringSourcePickerFieldComponent.SURGE_PERIOD_QUERY;
            }
        }

        if (this.dateQuery?.type === DateQueryTypeEnum.ABSOLUTE) {
            return;
        }
        const optionInList = this.dateFilterOptions.find(option =>
            DateQueryModel.equals(option.value.query, this.dateQuery)
        );
        if (!optionInList) {
            const hasCustom = this.dateFilterOptions.find(x => x.value.type === DateInputTypesEnum.custom);
            if (hasCustom) {
                return;
            }
            this.dateQuery = this.dateFilterOptions[0].value.query;
            this.dateQuery.timezone = this.timezone;
            await this.dateRangeChanged(this.dateQuery);
        }
    }

    /**
     * Selects the series data source
     */
    public async selectItem(item: DropdownOptionModel): Promise<void> {
        if ((this.selectedBlock?.displayElement as AnalysisDisplayElementModel)?.sourceBlockReferenceId) {
            delete (this.selectedBlock?.displayElement as AnalysisDisplayElementModel).sourceBlockReferenceId;
        }

        if (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) {
            this.usingCuratedMentions = false;

            this.clearSearch();

            if (!item) {
                this.value.id = null;
                this.value.dataSourceName = null;
                this.changes.emit(this.value);
                return;
            }

            this.value.id = item?.value?.backendDataSource?.id;
            this.value.referenceId = item?.value?.blockReferenceId;
            (this.selectedBlock?.displayElement as AnalysisDisplayElementModel).sourceBlockReferenceId = item?.value?.blockReferenceId;
            delete this.value.dataSourceName;

            if (item?.value?.mentions && !item?.value?.filterMentionSearchModel) {
                this.usingCuratedMentions = true;
                this.setNumberOfResults(this.setMentionCount(item?.value?.mentions));
            } else {
                this.setAutomaticSource(item?.value);
            }

            this.changes.emit(this.value);
        } else {
            super.selectItem(item);
        }
    }


    async setAutomaticSource(source) {
        let filterCriteria: MentionSearchCriteriaModel;
        let filterQuery: MentionSearchQueryModel;
        const oldSourceType = this.sourceType;

        this.sourceType = mentionSourceApiTypesEnumToMonitoringSeriesTypeEnum.get(source?.sourceType);
        if (source?.filterMentionSearchModel?.searchCriteria?.query?.dateQuery) {
            this.dateQuery = source?.filterMentionSearchModel.searchCriteria.query.dateQuery;
        }

        if (source?.filterMentionSearchModel?.searchCriteria) {
            filterCriteria = source?.filterMentionSearchModel?.searchCriteria;
            if (!source?.filterMentionSearchModel?.searchCriteria.query.isEmpty()) {
                filterQuery = source?.filterMentionSearchModel?.searchCriteria.query;

                // convert currency from USD to user currency as B.E currently only saves USD
                if (filterQuery?.ave?.min) {
                    filterQuery.ave.min = this.currencyConversionService.convertCurrencyWithoutISOCode(filterQuery?.ave?.min);
                }
                if (filterQuery?.ave?.max && filterQuery?.ave?.max !== Mention.maxAudienceReach) {
                    filterQuery.ave.max = this.currencyConversionService.convertCurrencyWithoutISOCode(filterQuery?.ave?.max);
                }
            }
        }

        this.getFiltersFromSearch(filterCriteria);
        this.filterService.setSearchFilters(filterCriteria);

        await this.updateSearchCriteria();
        if (source?.filterMentionSearchModel?.searchCriteria?.query?.dateQuery) {
            this.setSearchDateQuery(source?.filterMentionSearchModel?.searchCriteria.query.dateQuery);
        }

        this.mentionSearchService.setSortField(source?.filterMentionSearchModel?.searchCriteria.sortField);
        this.mentionSearchService.setSortOrder(source?.filterMentionSearchModel?.searchCriteria.sortOrder);
        if (filterCriteria) {
            this.search();
        }
        this.sourceType = oldSourceType;
    }

    public emitSuggestions(): void {
        if (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) {
            this.suggestions$.next(this.mentionBlocksOptions);
        } else {
            super.emitSuggestions();
        }
    }


    public initializeDataSourceOptions(): void {
        super.initializeDataSourceOptions();
        this.usingCuratedMentions = false;

        if (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) {
            this.suggestions$.next(this.mentionBlocksOptions);
        } else if (this.sourceType === MonitoringSeriesTypeEnum.allCuratedCoverageInBrief) {
            this.usingCuratedMentions = true;
            this.setNumberOfResults(this.allCuratedMentionsCount);
        }
    }

    /**
    //  * Sets the number of results to display as feedback
    //  * @param numberOfResults - number of mentions available
    //  */
    public setNumberOfResults(numberOfResults: number): void {
        if (
            (this.sourceType === MonitoringSeriesTypeEnum.coverageInSection) &&
            (this.selectedSource?.value?.pageSize < numberOfResults)
        ) {
            numberOfResults = this.selectedSource?.value?.pageSize;
        }

        super.setNumberOfResults(numberOfResults);
        this.emitChanges();
    }

    clearSearch(): void {
        this.mentionSearchService.setSortField(SearchCriteriaSortFieldEnum.published);
        this.mentionSearchService.setSortOrder(SortOrderEnum.descending);
        this.mentionSearchService.clearSearch();
    }

    emitChanges() {
        // selectedBlock counts are for page grid validation
        if (this.selectedBlock) {
            delete (this.selectedBlock.displayElement as AnalysisDisplayElementModel).curatedMentionCount;
            delete (this.selectedBlock.displayElement as AnalysisDisplayElementModel).dynamicMentionCount;
        }
        // this.value counts are for dialog validation
        delete this.value.curatedMentionCount;
        delete this.value.dynamicMentionCount;

        if (this.selectedBlock) {
            if (this.usingCuratedMentions) {
                (this.selectedBlock.displayElement as AnalysisDisplayElementModel).curatedMentionCount = this.numberOfResults;
            } else {
                (this.selectedBlock.displayElement as AnalysisDisplayElementModel).dynamicMentionCount = this.numberOfResults;
            }
        }
        if (this.usingCuratedMentions) {
            this.value.dynamicMentionCount = this.numberOfResults;
        } else {
            this.value.curatedMentionCount = this.numberOfResults;
        }
        this.changes.emit(this.value);
    }

    setMentionCount(mentions: Mention[] | MentionModel[]): number {
        // curated block use MentionModel from API while all mentions use F/E Mention
        const ids = [];
        mentions.forEach(mention => {
            if (mention.canGenerateInsight) {
                if (mention.mentionId) {
                    ids.push(mention.mentionId);
                } else if (mention.id) {
                    ids.push(mention.id);
                } else if (mention.mercuryId) {
                    ids.push(mention.mercuryId);
                }
            }
        });

        return [...new Set(ids)].length;
    }

    public async updateSearchCriteria(): Promise<void> {
        await super.updateSearchCriteria();
        this.mentionSearchService.setSearchCriteria(this.value.sourceSearchCriteria, this.sourceType);
    }

    public setSearchDateQuery(dateQuery: DateQueryModel): void {
        this.filterService.setDateQuery(dateQuery);
        this.mentionSearchService.setDateQuery(dateQuery);
    }
}
